// MobileStandings — phone view for the standings page (3v3 / 1v1 / EC).
//
// Topbar + segmented event tab live in the parent ProtoStandings (so the
// tab state survives a viewport change). This component only renders the
// body — three sub-views, picked by the parent's `tab` prop.
//
// 3v3: filter chips by group (A/B/C/D), then list of mw-stand-row rows.
//      Top-2 advance per group → top3 styling on rank.
// 1v1: single flat list of mw-stand-row rows ordered by Score; top-8
//      advance to bracket. No group chips.
// EC : list of rows showing rank + team + meta line (R1/R2/R3 · weight)
//      + final score on the right. Lowest round struck-through (visual
//      only — meta line text).

import type { NavigateFn } from '../../types';
import { teamById } from '../../mock/data';
import type { computeStandings3v3, computeStandings1v1, computeStandingsEC } from '../../state/standings';
import { usePersistentState } from '../../lib/usePersistentState';

export function MobileStandings3v3({ groups, onNavigate }: {
  groups: ReturnType<typeof computeStandings3v3>;
  onNavigate?: NavigateFn;
}) {
  const groupNames = groups.map((g) => g.name);
  // Persist so back-navigation from team detail lands on the last group.
  const [groupFilter, setGroupFilter] = usePersistentState<string>(
    'standings.group',
    groupNames[0] ?? 'A',
  );
  const activeGroup = groups.find((g) => g.name === groupFilter) ?? groups[0];
  const rows = activeGroup?.rows ?? [];
  return (
    <>
      {groupNames.length > 1 && (
        <div className="mw-chips" style={{ borderBottom: 'var(--bw) solid var(--line)' }}>
          {groupNames.map((g) => (
            <div
              key={g}
              className={`mw-chip ${groupFilter === g ? 'active' : ''}`}
              onClick={() => setGroupFilter(g)}
            >GROUP {g}</div>
          ))}
        </div>
      )}
      <div className="mw-body">
        {rows.length === 0 ? (
          <div className="mw-empty">NO TEAMS IN GROUP {groupFilter}</div>
        ) : (
          rows.map((r) => {
            const t = teamById(r.team);
            // top-2 per group advance — yellow rank + yellow row tint.
            const isAdvancing = !!r.advance;
            // Meta line collects only "extras" — penalty + BO3 marker.
            // Group is omitted (a group filter chip already scopes the
            // list), and the W-L-T record now lives inline with the team
            // name above. Render the row only when something's there.
            const metaParts: string[] = [];
            metaParts.push(`VPΔ ${r.op - r.dp >= 0 ? '+' : ''}${r.op - r.dp}`);
            if (r.penalty) metaParts.push(`−${r.penalty} PENALTY`);
            if (r.needsTiebreaker) metaParts.push('BO3?');
            return (
              <div
                key={`${r.team}-${r.rank}`}
                className={`mw-stand-row ${isAdvancing ? 'advance' : ''}`}
              >
                <div className={`mw-stand-rank ${isAdvancing ? 'top3' : ''}`}>{r.rank}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="mw-stand-team-row">
                    <span
                      className={`mw-stand-team ${onNavigate ? 'team-name-link' : ''}`}
                      onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                      title={onNavigate ? `Open ${t.name}` : undefined}
                    >{t.short}</span>
                    <span className="mw-stand-record">{r.w}W-{r.l}L-{r.t}T</span>
                  </div>
                  {metaParts.length > 0 && (
                    <div className="mw-stand-meta">{metaParts.join(' · ')}</div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mw-stand-wl">{r.rp}</div>
                  <div className="mw-stand-diff" style={{ color: 'var(--text-2)' }}>SCORE</div>
                </div>
              </div>
            );
          })
        )}
        <div
          className="mono"
          style={{
            padding: '12px 14px',
            fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.08em', lineHeight: 1.5,
          }}
        >
          BO2 SCORE: W+W = 3, W+T = 1, T+T = 0, L+T = 0, L+L = 0 · TIES BROKEN BY H2H → VP DIFFERENTIAL → BO3. TOP 2 PER GROUP ADVANCE.
        </div>
        <div className="mw-tail-spacer" />
      </div>
    </>
  );
}

export function MobileStandings1v1({ swiss, onNavigate }: {
  swiss: ReturnType<typeof computeStandings1v1>;
  onNavigate?: NavigateFn;
}) {
  return (
    <>
      <div className="mw-body">
        <div className="mw-list-head">
          <span>SWISS · ROUND {swiss.round} OF {swiss.totalRounds}</span>
          <span>TOP 8 ADVANCE</span>
        </div>
        {swiss.rows.length === 0 ? (
          <div className="mw-empty">NO 1V1-ELIGIBLE TEAMS</div>
        ) : (
          swiss.rows.map((r) => {
            const t = teamById(r.team);
            const isAdvancing = !!r.advance;
            const metaParts: string[] = [];
            metaParts.push(`CUM ${r.cumulativeScore}`, `MED ${r.medianBuchholz}`);
            if (r.needsTiebreaker) metaParts.push('BO3?');
            return (
              <div
                key={`${r.team}-${r.rank}`}
                className={`mw-stand-row ${isAdvancing ? 'advance' : ''}`}
              >
                <div className={`mw-stand-rank ${isAdvancing ? 'top3' : ''}`}>{r.rank}</div>
                <div style={{ minWidth: 0 }}>
                  <div className="mw-stand-team-row">
                    <span
                      className={`mw-stand-team ${onNavigate ? 'team-name-link' : ''}`}
                      onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                      title={onNavigate ? `Open ${t.name}` : undefined}
                    >{t.short}</span>
                    <span className="mw-stand-record">{r.w}W-{r.l}L-{r.t}T</span>
                  </div>
                  {metaParts.length > 0 && (
                    <div className="mw-stand-meta">{metaParts.join(' · ')}</div>
                  )}
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div className="mw-stand-wl">{r.rp}</div>
                  <div className="mw-stand-diff" style={{ color: 'var(--text-2)' }}>SCORE</div>
                </div>
              </div>
            );
          })
        )}
        <div
          className="mono"
          style={{
            padding: '12px 14px',
            fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.08em', lineHeight: 1.5,
          }}
        >
          SCORE = SUM OF MATCH POINTS. TIES BROKEN BY H2H → CUMULATIVE → MEDIAN BUCHHOLZ.
        </div>
        <div className="mw-tail-spacer" />
      </div>
    </>
  );
}

export function MobileStandingsEC({ rows, onNavigate }: {
  rows: ReturnType<typeof computeStandingsEC>;
  onNavigate?: NavigateFn;
}) {
  return (
    <>
      <div className="mw-body">
        <div className="mw-list-head">
          <span>ENGINEER CHALLENGE</span>
          <span>{rows.length} TEAMS</span>
        </div>
        {rows.length === 0 ? (
          <div className="mw-empty">NO EC-REGISTERED TEAMS</div>
        ) : (
          rows.map((r) => {
            const t = teamById(r.team);
            const isLeader = r.rank === 1 && r.finalScore > 0;
            const rs = r.roundScores;
            // Display the 3 round scores as "12 / 8 / 15", with the lowest
            // dropped (rendered with a strike). EC tiebreaker uses weight,
            // shown after the round breakdown.
            const droppedIdx = rs.length === 3 ? rs.indexOf(Math.min(...rs)) : -1;
            const formattedRounds = [0, 1, 2]
              .map((i) => {
                const v = rs[i];
                if (v == null) return '—';
                return i === droppedIdx ? `(${v})` : `${v}`;
              })
              .join(' / ');
            const weightText = Number.isNaN(r.weightKg) ? '—' : `${r.weightKg}KG`;
            return (
              <div
                key={r.team}
                className={`mw-stand-row ${isLeader ? 'advance' : ''}`}
              >
                <div className={`mw-stand-rank ${isLeader ? 'top3' : ''}`}>{r.rank}</div>
                <div style={{ minWidth: 0 }}>
                  <div
                    className={`mw-stand-team ${onNavigate ? 'team-name-link' : ''}`}
                    onClick={onNavigate ? () => onNavigate('teams', { team: r.team }) : undefined}
                    title={onNavigate ? `Open ${t.name}` : undefined}
                  >{t.short}</div>
                  <div className="mw-stand-meta">{formattedRounds} · {weightText}</div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  {/* For EC, no-score rows render '—' in muted; otherwise the
                   *   green-score color from the .mw-stand-wl base rule applies. */}
                  <div className="mw-stand-wl" style={r.finalScore > 0 ? undefined : { color: 'var(--text-3)' }}>
                    {r.finalScore > 0 ? r.finalScore.toFixed(1) : '—'}
                  </div>
                  <div className="mw-stand-diff" style={{ color: 'var(--text-2)' }}>SCORE</div>
                </div>
              </div>
            );
          })
        )}
        <div
          className="mono"
          style={{
            padding: '12px 14px',
            fontSize: 9, color: 'var(--text-3)', letterSpacing: '0.08em', lineHeight: 1.5,
          }}
        >
          SCORE = AVG OF BEST 2 OF 3 ROUND SCORES · LOWEST ROUND DROPPED · TIES BROKEN BY WEIGHT
        </div>
        <div className="mw-tail-spacer" />
      </div>
    </>
  );
}
